import users_api from "../../api/users";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import GenericDialog from "../../components/GenericDialog";
import GenericInput from "../../components/GenericInput";
import GenericLabel from "../../components/GenericLabel";
import textUtils from "../../utils/TextUtils";
import dateUtils from "../../utils/DateUtils";
import memberships from "../../models/Memberships";
import subscriptionUtils from "../../utils/SubscriptionUtils";
import auth from "../../api/auth";

const UserDetails = () => {
    const navigate = useNavigate()
    const params = useParams();

    // User Info
    const [emailParam, setEmailParam] = useState("")
    const [userData, setUserData] = useState()
    const [accreditationData, setAccreditationData] = useState()

    // Modifiable Data
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [coachingOrganisation, setCoachingOrganisation] = useState("")
    const [emailValidated, setEmailValidated] = useState("")

    // Modify Subscription
    const [modifySubscription, setModifySubscription] = useState(false)
    const [dropdownMembershipTier, setDropdownMembershipTier] = useState()
    const [membershipTier, setMembershipTier] = useState()
    const [membershipStartDate, setMembershipStartDate] = useState()
    const [membershipExpiryDate, setMembershipExpiryDate] = useState()

    // Display Data
    const [currentMembership, setCurrentMembership] = useState()
    const [stripeID, setStripeID] = useState("")
    const [accessCode, setAccessCode] = useState({})
    const [country, setCountry] = useState("")
    const [marketingConsent, setMarketingConsent] = useState("")
    const [mfaEnabled, setMfaEnabled] = useState("")
    const [lastLogin, setLastLogin] = useState()

    // Error Dialog
    const [dialogTitle, setDialogTitle] = useState("")
    const [dialogText, setDialogText] = useState("")
    const buttons = [{"text":"OK", "id": "1"}];
    const [redirect, setRedirect] = useState(false)

    // Delete Dialog
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // User Notes
    const [currentNotes, setCurrentNotes] = useState([])
    const [note, setNote] = useState("")

    useEffect(() => {
        auth.hasSession().then((session) => {
            if (!session) window.location.assign("/logout");
                    });
    }, [])

    useEffect(() => {
        if (!params.user_email) navigate("/users");
        setEmailParam(params.user_email)
    }, [params.user_email]);

    useEffect(() => {
        GetUser()
        GetAccreditation()
        GetNotes()
    }, [emailParam]);

    const GetUser = () => {
        if (!emailParam) return
        let response = users_api.GetUser(emailParam)
        response.then(
            response => {
                // Page items
                let data = response.data.users[0]
                setUserData(data)


                // Set data
                setFirstName(data.firstname)
                setLastName(data.lastname)
                setEmail(data.email)
                setCoachingOrganisation(data.coaching_organisation)
                setCurrentMembership(data.subscription_details)
                setEmailValidated(data.email_validated)
                setStripeID(data.stripe_id)
                setCountry(data.country)
                setMarketingConsent(data.marketing_consent)
                setMfaEnabled(data.mfa_enabled)
                setLastLogin(data.last_seen)

                // Access Code data
                let access_code = {
                    "code": data.access_code && data.access_code ? data.access_code : "No code",
                    "tier": data.access_code_membership && data.access_code_membership
                        ? textUtils.titleCase(subscriptionUtils.SubscriptionTypeToString(data.access_code_membership))
                        : "N/A",
                    "tier_length": data.access_code_membership_length && data.access_code_membership_length
                        ? data.access_code_membership_length / 86400 : "N/A",
                    "discount": data.access_code_membership_discount && data.access_code_membership_discount
                        ? data.access_code_membership_discount : 0,
                    "discount_membership_type": data.access_code_membership_discount && data.access_code_membership_discount ?
                        (data.access_code_membership_discount_type && data.access_code_membership_discount_type
                            ? textUtils.titleCase(subscriptionUtils.SubscriptionTypeToString(data.access_code_membership_discount_type))
                            : "All Memberships")
                        : "N/A",
                    "force_stripe": data.access_code_membership_force_stripe && data.access_code_membership_force_stripe
                        ? "Yes"
                        : "No"
                }
                setAccessCode(access_code)
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error fetching users. Please try again")
        })
    }

    const GetAccreditation = () => {
        if (!emailParam) return
        let response = users_api.GetAccreditation(emailParam)
        response.then(
            response => {
                // Page items
                let data = response.data.accreditations[0]
                setAccreditationData(data)
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error fetching users accreditation. Please try again")
        })
    }


    const modifyMembership = () => {
        // Checks if membership tier has been set
        if ((!membershipTier && membershipTier !== 0)) {
            setDialogTitle("Modification Error")
            setDialogText("Please ensure you have selected a membership tier")
            return
        }
        // Create basic body
        let body = {
            "email": email && email ? email : emailParam,
            "type": membershipTier,
        }

        // If membership is bronze don't add dates otherwise check if dates have been selected and add dates
        if (membershipTier === 0) {
            body["start_date"] = "n/a"
            body["end_date"] = "n/a"
        }
        else {
            if (!membershipExpiryDate || !membershipStartDate) {
                setDialogTitle("Modification Error")
                setDialogText("Please ensure you have selected a start date and expiry date")
                return
            }
            body["start_date"] = dateUtils.date_to_timestamp(membershipStartDate)
            body["end_date"] = dateUtils.date_to_timestamp(membershipExpiryDate)
        }
        // Send request
        let response = users_api.PostSubscription(body)
        response.then(
            response => {
                setModifySubscription(false)
                GetUser()
            }
        ).catch((err) => {
            setDialogTitle("Modification Error")
            setDialogText("There was an error modifying the membership. Please try again")
        })
    }

    const modifyUser = (overrideEmailValidated) => {
        const validated = overrideEmailValidated ?? emailValidated

        if (!firstName || !lastName || !email || !coachingOrganisation || validated === null || validated === undefined) {
            setDialogTitle("Modification Error")
            setDialogText("Please ensure you have valid info fields for the user.")
            return
        }

        let body = {
            email,
            firstname: firstName,
            lastname: lastName,
            coaching_organisation: coachingOrganisation,
            email_validated: validated
        }

        users_api.PutUser(emailParam, body)
            .then(response => {
                let new_email = response.data.email
                navigate("/users/" + new_email)
            })
            .catch(() => {
                setDialogTitle("Modification Error")
                setDialogText("There was an error modifying the user information. Please try again")
            })
    }

    const confirmDeleteUser = () => {
        setShowDeleteConfirm(true);
    };

    const deleteUser = () => {
        users_api.DeleteUser(emailParam)
            .then(() => navigate("/users"))
            .catch(() => {
                setDialogTitle("Delete Error");
                setDialogText("There was an error deleting the user. Please try again.");
            })
            .finally(() => setShowDeleteConfirm(false));
    };

    const handleValidateEmail = () => {
        setEmailValidated(1)       // update UI
        modifyUser(1)              // send value immediately
    }


    const handleErrorDialogResult = (res) => {
        if(!res) return;
        if (redirect && redirect) navigate("/users")
        setDialogText("")
        setDialogTitle("")
    }

    const handleMembershipSelect = (ev) => {
        setDropdownMembershipTier(ev)
        setMembershipTier(ev.id)
    }

    const handleStartMembershipModify = () => {
        setModifySubscription(true)
    }

    const verifyAccreditation = (accreditation_id) => {
        if (!accreditation_id) return
        let response = users_api.VerifyAccreditation(accreditation_id)
        response.then(
            response => {
                GetAccreditation()
                GetUser()
            }
        ).catch((err) => {
            setDialogTitle("Updating Error")
            setDialogText("There was an error verifying the users accreditation. Please try again")
        })
    }

    const declineAccreditation = (accreditation_id) => {
        if (!accreditation_id) return
        let response = users_api.declineAccreditation(accreditation_id)
        response.then(
            response => {
                GetAccreditation()
                GetUser()
            }
        ).catch((err) => {
            setDialogTitle("Updating Error")
            setDialogText("There was an error un-verifying the users accreditation. Please try again")
        })
    }

    const downloadAccreditation = (users_email) => {
        let response = users_api.DownloadAccreditationFile(users_email)
        response.then(
            response => {
                const base64_link = response.data.data;
                const file_name = response.data.filename;
                const anchorElement = document.createElement('a');
                anchorElement.href = base64_link;
                anchorElement.download = file_name;
                anchorElement.click();
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error getting the file for download")
        })
    }

    const GetNotes = () => {
        if (!emailParam) return
        let response = users_api.GetNotes(emailParam)
        response.then(
            response => {
                let data = response.data.notes
                setCurrentNotes(data)
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error getting users notes. Please try again")
        })
    }

    const addNote = () => {
        if (!emailParam) return
        let body = {
            "comment": note
        }
        let response = users_api.AddNote(emailParam, body)
        response.then(
            response => {
                setNote("")
                GetNotes()
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error adding note. Please try again")
        })
    }

    const handleDeleteSubscriptions = () => {
        if (!emailParam) return
        let response = users_api.RemoveSubscription(emailParam)
        response.then(
            response => {
                GetUser()
            }
        ).catch((err) => {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error removing the users subscription. Please try again")
        })
    }

    const membershipMismatchNotes = userData && currentMembership
        ? subscriptionUtils.getTierMismatchNote(
            userData.subscription,
            currentMembership.type,
            userData.verified,
            userData.accred_declined,
            currentMembership.expired || currentMembership.status === 3
        )
        : null

    return (
        <div className="page-container">
            <div className="page-title">
                {userData && userData ? "User: " + emailParam : "User Not Found"}
            </div>
            <div className="page-subtitle">
                Info
            </div>
            <div className="form-container">
                <GenericInput label="First Name" value={firstName} updateValue={setFirstName}/>
                <GenericInput label="Last Name" value={lastName} updateValue={setLastName}/>
                <GenericInput label="Email" value={email} updateValue={setEmail}/>
                {emailValidated === 0 && (
                    <>
                        <div className="validate-email-user-details">
                            Email not validated
                        </div>
                        <div className="user-membership-modify submit button link"
                             onClick={handleValidateEmail}>
                            Validate Email
                        </div>
                    </>
                )}

                {emailValidated === 1 && (
                    <div className = "validate-email-user-details validated" >
                        Email validated
                    </div>
                )}
                <GenericInput label="Coaching Organisation" value={coachingOrganisation}
                              updateValue={setCoachingOrganisation}/>
            </div>
            <div className={"user-membership-modify submit button link"}
                 onClick={() => modifyUser()}>
                Update
            </div>
            <div className={"user-membership-delete submit button link"}
                 onClick={() => confirmDeleteUser()}>
                Delete
            </div>
            <div className="form-container">
                <GenericLabel label="Country" value={country || ""}/>
                <GenericLabel label="Marketing Consent" value={marketingConsent == 1 ? "True" : "False"}/>
                <GenericLabel label="MFA Enabled" value={mfaEnabled == 1 ? "True" : "False"}/>
                <GenericLabel label="Last Login Date" value={lastLogin ? dateUtils.display_date(lastLogin) : "N/A"}/>
            </div>
            <div className="spacer"/>

            <div className="page-subtitle">
                Access Code:
            </div>
            <div className="table-container">
                <div className="table-row">
                    <div className="table-column heading">Code Used</div>
                    <div className="table-column heading">Tier</div>
                    <div className="table-column heading">Length (days)</div>
                    <div className="table-column heading">Discount</div>
                    <div className="table-column heading">Discount Membership Type</div>
                    <div className="table-column heading last">Force Stripe</div>
                </div>
                <div className="table-row">
                    <div className="table-column">{accessCode && accessCode.code}</div>
                    <div className="table-column">{accessCode && accessCode.tier}</div>
                    <div className="table-column">{accessCode && accessCode.tier_length}</div>
                    <div className="table-column">{accessCode && accessCode.discount}%</div>
                    <div className="table-column">{accessCode && accessCode.discount_membership_type}</div>
                    <div className="table-column last">{accessCode && accessCode.force_stripe}</div>
                </div>
            </div>
            <div className="spacer"/>

            <div className="page-subtitle">
                Membership
            </div>
            <div className="form-container">
                <div className="generic-label-container">
                    <div className="label">Cached Subscription</div>
                    <div className="label-content">
                        <div className={subscriptionUtils.getTierBadgeClass(
                            userData && userData.subscription_level ? userData.subscription_level : userData && userData.subscription
                        )}>
                            {userData && subscriptionUtils.getTierDisplayName(
                                userData.subscription_level ? userData.subscription_level : userData.subscription
                            )}
                        </div>
                    </div>
                </div>
                <div className="generic-label-container">
                    <div className="label">Paid Membership</div>
                    <div className="label-content">
                        <div className={subscriptionUtils.getTierBadgeClass(
                            currentMembership && currentMembership.type !== undefined
                                ? currentMembership.type
                                : currentMembership && currentMembership.name
                        )}>
                            {currentMembership && subscriptionUtils.getTierDisplayName(
                                currentMembership.type !== undefined ? currentMembership.type : currentMembership.name
                            )}
                        </div>
                    </div>
                </div>
                {membershipMismatchNotes ? membershipMismatchNotes.map((note, index) => (
                    <div className="note membership-tier-note" key={index}>
                        {note}
                    </div>
                )) : null}
                <GenericLabel label="Membership Start" value={currentMembership && currentMembership.start_date
                    ? dateUtils.display_date(currentMembership.start_date) : "N/A"}/>
                <GenericLabel label="Membership Expiry" value={currentMembership && currentMembership.end_date
                    ? dateUtils.display_date(currentMembership.end_date) : "N/A"}/>
                {modifySubscription && modifySubscription
                    ?
                    <>
                        <div className="page-sub-subtitle">
                            New Membership
                        </div>
                        <GenericInput type="dropdown" label="Membership Tier" options={memberships.addable_memberships}
                                      value={dropdownMembershipTier} updateValue={handleMembershipSelect.bind(this)}/>
                        {dropdownMembershipTier && dropdownMembershipTier.id ?
                            <>
                                <GenericInput type="date" label="Membership Start"
                                              value={membershipStartDate} updateValue={setMembershipStartDate}/>
                                <GenericInput type="date" label="Membership Expiry"
                                              value={membershipExpiryDate} updateValue={setMembershipExpiryDate}/>
                            </>
                            :
                            null
                        }
                        <div className="modify-subscription-button-container">
                            <div className={"user-membership-modify submit button link"}
                                 onClick={() => modifyMembership()}>
                                Update Membership
                            </div>
                            <div className="user-membership-modify cancel button grey link"
                                 onClick={() => setModifySubscription(false)}>
                                Cancel
                            </div>
                        </div>
                        <div className="note">
                            <span>*</span> This only changes the subscription tier of the user for the site for the
                            period entered and does not modify the payments the user is making for future
                            subscriptions. To adjust the payments, use the Stripe Portal
                        </div>
                    </>
                    :
                    <>
                        <div className="user-membership-modify button link"
                             onClick={() => handleStartMembershipModify()}>
                            Add Membership
                        </div>
                    </>
                }

                <GenericLabel label="Stripe Customer ID" value={stripeID}/>
            </div>
            <div className="spacer"/>
            <div className="form-container">
                <div className="user-membership-modify button link"
                     onClick={() => handleDeleteSubscriptions()}>
                    Delete Subscriptions*
                </div>
                * This only updates our local database removing the users subscription. You will need to go into Stripe
                prior and manually remove their subscription for this to work.
            </div>
            <div className="spacer"/>

            <div className="page-subtitle">
                Accreditation
            </div>
            {accreditationData && accreditationData ?
                <>
                    <div className="table-container">
                        <div className="table-row">
                            <div className="table-column heading">Type</div>
                            {accreditationData && accreditationData.type === "EMCC" ?
                                <>
                                    <div className="table-column heading">Name</div>
                                    <div className="table-column heading">Certificate Number</div>
                                    <div className="table-column heading">Award Level</div>
                                </>
                                :
                                <>
                                    <div className="table-column heading">File</div>
                                </>
                            }
                            <div className="table-column heading">Verified</div>
                            <div className="table-column heading last">Verified by</div>
                        </div>
                        <div className="table-row">
                            <div className="table-column">{accreditationData && accreditationData.type}</div>
                            {accreditationData && accreditationData.type === "EMCC" ?
                                <>
                                    <div className="table-column">{accreditationData && accreditationData.name}</div>
                                    <div
                                        className="table-column">{accreditationData && accreditationData.certificate_number}</div>
                                    <div
                                        className="table-column">{accreditationData && accreditationData.award_level}</div>
                                </>
                                :
                                <div className="table-column">
                                    <div className={"user-accreditation-download submit button link"}
                                         onClick={() => downloadAccreditation(emailParam)}>
                                        Download File
                                    </div>
                                </div>
                            }
                            <div className="table-column">
                                {accreditationData && accreditationData.verified === 1 ? (
                                    <div className="verification-container verified">
                                        Verified
                                    </div>
                                ) : (
                                    <div className="verification-container">
                                        {userData && userData.accred_declined === 1 ? "Declined" : "Not Verified"}
                                    </div>
                                )}
                            </div>
                            <div
                                className="table-column last">{accreditationData && accreditationData.verified_by ? accreditationData.verified_by : "N/A"}</div>
                        </div>
                    </div>

                    <div className="modify-subscription-button-container">
                        <div className={"user-membership-modify submit button link"}
                             onClick={() => verifyAccreditation(accreditationData.id)}>
                            Verify
                        </div>
                        <div className={"user-membership-modify submit button link"}
                             onClick={() => declineAccreditation(accreditationData.id)}>
                            Decline Accreditation
                        </div>
                    </div>
                </>
                :
                "The user has not submitted an accreditation request yet"
            }
            <div className="spacer"/>

            <div className="page-subtitle">
                Notes
            </div>
            {currentNotes && currentNotes.map((note) => {
                return (
                    <div className="user-note">
                        <div className="note comment">"{note.comment}"</div>
                        <div className="note-info-container">
                            <div className="note user bold">{note.user}</div>
                            <div className="note date bold">{dateUtils.display_date_short(note.timestamp)}</div>
                        </div>
                    </div>
                )
            })}

            <div className="notes-form-container">
                <div className="input-container">
                    <textarea className="text-input-container" value={note}
                              onChange={(ev) => {
                                  setNote(ev.target.value)
                              }}/>
                </div>
            </div>
            <div className="modify-subscription-button-container">
                <div className={"user-membership-modify submit button link"}
                     onClick={() => addNote()}>
                    Add Note
                </div>
            </div>


            <div className="spacer"/>
            {dialogText && dialogText ? <GenericDialog title={dialogTitle} text={dialogText} buttons={buttons}
                                                       result={handleErrorDialogResult.bind(this)}/> : null}
            {showDeleteConfirm ? (
                <>
                    <div className="page-mask"/>
                    <div className="dialog-container">
                        <div className="dialog-header-container">
                            <div className="dialog-header">
                                Confirm Delete
                            </div>
                            <div className="dialog-text">
                                Are you sure you want to permanently delete <strong>{email}</strong>?<br/>
                                This action cannot be undone.
                            </div>
                            <div className="dialog-footer-container">
                                <div
                                    className="dialog button link"
                                    onClick={deleteUser}>
                                    <div className="dialog-button-text">Delete</div>
                                </div>
                                <div
                                    className="dialog button link"
                                    onClick={() => setShowDeleteConfirm(false)}>
                                    <div className="dialog-button-text">Cancel</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}

export default UserDetails